import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneIcon from '@mui/icons-material/Done';
import mixpanel from 'mixpanel-browser';

import NavBar from '../../components/NavBar';

import { OpenModalButton } from './OpenModalButton';
import { OpenSurveyButton } from './OpenSurveyButton';

import { ModalPopup } from '../Elements/Modal';
import { ModalButtonContent } from '../DashboardUserInfoRow/ModalButtonContent';
import { GeneralSurveyModalContent } from './GeneralSurvey/GeneralSurveyModalContent';

import { SignUpOrLoginModalContent } from '../Forms/SignUpOrLoginModalContent';
import { postSurvey, setUserInfo } from '../../actions';
import { InfoTooltip } from '../Elements/Tooltip';

import { useLocalStorage } from '../../CustomHooks/useLocalStorage';

import { getOrSetAnonymousUserId } from '../../utils';

import { TOKEN, LOGOUT, handleSettingsClick } from '../../constants';

import {
  ALL_SURVEY_QUESTIONS,
  GENERAL_QUESTIONS_TITLE,
  UI_EXPERIENCE_TITLE,
  AUTO_SURVEY_QUESTIONS_TITLE,
  AUTO_LOANS_SURVEY_QUESTIONS_TITLE,
  AUTO_INSURANCE_SURVEY_QUESTIONS_TITLE,
  HOME_SURVEY_QUESTIONS_TITLE,
  HOME_MORTGAGE_SURVEY_QUESTIONS_TITLE,
  HOME_INSURANCE_SURVEY_QUESTIONS_TITLE,
  FEDERAL_CREDIT_UNION,
  AUTO_RATES_FILTER,
  MORTGAGE_RATES_FILTER,
  BACK,
  SKIP,
  NEXT,
  THANK_YOU_MESSAGE,
  SUBMIT,
  OPEN,
  SIGNED_IN_MODAL_ON_SURVEY,
  MODAL_BUTTON_TITLE,
  MODAL_BUTTON_ICON,
  MODAL_BUTTON_ID,
} from '../../constants/Surveys';

import { getSurveyQuestionsCompletedHeader } from '../../constants/Surveys/GeneralSurveyModalContent';

import { surveyText } from '../../constants/Footer/survey';

import { Footer } from '../Footer/Footer';

import './surveys.css';

const steps = [
  {
    label: GENERAL_QUESTIONS_TITLE,
  },
  {
    label: UI_EXPERIENCE_TITLE,
  },
  {
    label: AUTO_SURVEY_QUESTIONS_TITLE,
  },
  {
    label: AUTO_LOANS_SURVEY_QUESTIONS_TITLE,
  },
  {
    label: AUTO_INSURANCE_SURVEY_QUESTIONS_TITLE,
  },
  {
    label: HOME_SURVEY_QUESTIONS_TITLE,
  },
  {
    label: HOME_MORTGAGE_SURVEY_QUESTIONS_TITLE,
  },
  {
    label: HOME_INSURANCE_SURVEY_QUESTIONS_TITLE,
  },
  {
    label: FEDERAL_CREDIT_UNION,
  },
  {
    label: AUTO_RATES_FILTER,
  },
  {
    label: MORTGAGE_RATES_FILTER,
  },
];

export const Surveys = () => {
  const { user, jwt } = useSelector(({ userReducer: { user, jwt } }) => ({
    user,
    jwt,
  }));

  const [activeStep, setActiveStep] = useState(0);
  const [surveyQuestion, setSurveyQuestions] = useState(ALL_SURVEY_QUESTIONS);
  const [isOpen, setIsOpen] = useState(true);
  const [isLoginOrSignUpModalOpen, setIsLoginOrSignUpModalOpen] = useState(false);
  const [isSignIn, setIsSignIn] = useState(false);

  const dispatch = useDispatch();

  const [value] = useLocalStorage(TOKEN);

  const isLastStep = activeStep === steps.length - 1;

  const isCurrentSurveyCompleted = surveyQuestion[steps?.[activeStep]?.label].completed;
  const SKIP_OR_NEXT = isCurrentSurveyCompleted ? NEXT : SKIP;

  useEffect(() => {
    setIsLoginOrSignUpModalOpen(true);
    if (value || (jwt && !user.id)) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }
      const user = atob(localStorage.getItem(TOKEN)?.split('.')?.[1]);
      const parsedUser = JSON.parse(user);

      if (parsedUser) {
        const date = new Date(parsedUser.exp * 1000);
        const now = new Date();
        if (date < now) {
          handleSettingsClick[LOGOUT]?.(dispatch, parsedUser);
        }
        setIsLoginOrSignUpModalOpen(false);
        dispatch((dispatch) => setUserInfo(dispatch, parsedUser));
      }
    } else {
      const anonymousUserId = getOrSetAnonymousUserId();

      dispatch((dispatch) => setUserInfo(dispatch, { anonymousUserId }));
    }
  }, [value, jwt, isSignIn]);

  useEffect(() => {
    if (user?.email) {
      setIsLoginOrSignUpModalOpen(false);
      setIsSignIn(true);
    }
  }, [user?.email]);

  useEffect(() => {
    if (isSignIn && (user?.id || !user?.email)) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }
      const user = atob(localStorage.getItem(TOKEN)?.split('.')?.[1]);
      const parsedUser = JSON.parse(user);

      if (parsedUser) {
        const date = new Date(parsedUser.exp * 1000);
        const now = new Date();
        if (date < now) {
          handleSettingsClick[LOGOUT]?.(dispatch);
        }
        setIsLoginOrSignUpModalOpen(false);
        dispatch((dispatch) => setUserInfo(dispatch, parsedUser));
      }
    }
  }, [isSignIn, user?.id]);

  const handleNext = () => {
    if (isLastStep) {
      return;
    }

    setActiveStep((prevActiveStep) => {
      mixpanel.track('Survey Active Step', {
        user,
        survey_title: steps?.[activeStep]?.label,
        currentStepObj: surveyQuestion[steps?.[activeStep]?.label],
        action: SKIP_OR_NEXT,
      });

      return prevActiveStep + 1;
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      mixpanel.track('Survey Active Step', {
        user,
        survey_title: steps?.[activeStep]?.label,
        currentStepObj: surveyQuestion[steps?.[activeStep]?.label],
        action: BACK,
      });

      return prevActiveStep - 1;
    });
  };

  const handleContinue = ({ currentObj, label }) => {
    const updatedObj = { ...currentObj, skipped: false, completed: true };

    setSurveyQuestions((prevState) => {
      return {
        ...prevState,
        [label]: updatedObj,
      };
    });

    dispatch(
      postSurvey({
        surveyType: label,
        surveyData: { [label]: updatedObj },
        token: jwt || value,
        user,
      }),
    );
    mixpanel.track('Survey Active Step', {
      user,
      survey_title: label,
      currentStepObj: updatedObj,
      action: SUBMIT,
    });

    setIsOpen(false);

    setActiveStep((prevActiveStep) => {
      if (isLastStep) {
        return 0;
      }
      return prevActiveStep + 1;
    });

    mixpanel.track('Survey Active Step', {
      user,
      survey_title: steps?.[activeStep]?.label,
      currentStepObj: surveyQuestion[steps?.[activeStep]?.label],
      action: SUBMIT,
    });
  };

  const handleRenderSurveyQuestions = () => {
    mixpanel.track('Survey Active Step', {
      user,
      survey_title: steps?.[activeStep]?.label,
      currentStepObj: surveyQuestion[steps?.[activeStep]?.label],
      action: OPEN,
    });
    setIsOpen(true);
  };

  const subtitleTooltip = (subtitle, tooltipKey) => {
    const words = subtitle.split(' ');
    return words.map((word, index) => {
      const isLast = index === words.length - 1;
      return (
        <span className="subtitle-word-wrapper" key={`${word}-${index}`}>
          {isLast ? (
            <span className="tooltip-wrapper">
              <p className="subtitle-word">{word}</p>
              <InfoTooltip contentKey={tooltipKey} />
            </span>
          ) : (
            <p className="subtitle-word">{word}</p>
          )}
        </span>
      );
    });
  };

  return (
    <div>
      <NavBar currentPath="/survey" />
      <div className="survey-page-header-section-wrapper">
        <h1 className="survey-page-header-wrapper">{surveyText.title}</h1>
        {/* <p className="survey-page-subheader-wrapper">{surveyText.subtitle}</p> */}
        <div>{subtitleTooltip(surveyText.subtitle, 'rewardPayment')}</div>
      </div>
      <ModalPopup
        buttonContent={
          <ModalButtonContent
            modalButtonTitle={SIGNED_IN_MODAL_ON_SURVEY[isSignIn]()[MODAL_BUTTON_TITLE]}
            modalButtonIcon={SIGNED_IN_MODAL_ON_SURVEY[isSignIn]()[MODAL_BUTTON_ICON]}
            isConfirmed={isSignIn}
          />
        }
        modalContent={<SignUpOrLoginModalContent path="/survey" user={user || {}} />}
        title={''}
        modalButtonId={SIGNED_IN_MODAL_ON_SURVEY[isSignIn]()[MODAL_BUTTON_ID]}
        handleOpen={() => !isSignIn && setIsLoginOrSignUpModalOpen(true)}
        handleClose={() => setIsLoginOrSignUpModalOpen(false)}
        open={!isSignIn ? isLoginOrSignUpModalOpen : false}
      />
      <div className="survey-stepper-wrapper">
        <Box sx={{ maxWidth: 1600, ml: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step
                key={step.label}
                sx={{
                  '& .Mui-completed': { color: 'black !important' },
                  '& .Mui-active': { color: 'black !important' },
                }}
              >
                <StepLabel>
                  {step.label} {surveyQuestion[step.label].completed && <DoneIcon />}
                </StepLabel>
                <StepContent>
                  <Typography>{step.description}</Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1, color: 'black', borderColor: 'black' }}
                    >
                      <ArrowBackIcon /> {BACK}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleNext}
                      size="small"
                      sx={{ mt: 1, mr: 1, color: 'black', borderColor: 'black' }}
                      disabled={isLastStep}
                    >
                      <SkipNextIcon /> {SKIP_OR_NEXT}
                    </Button>
                    <OpenModalButton
                      questions={surveyQuestion?.[steps?.[activeStep]?.label]}
                      label={step.label}
                      modalTitle={step.label}
                      handleContinue={handleContinue}
                      isLastStep={isLastStep}
                    />
                    <OpenSurveyButton
                      modalTitle={step.label}
                      onOpen={handleRenderSurveyQuestions}
                      isOpen={isOpen}
                    />
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
        {isOpen && (
          <div className="survey-questions-page-wrapper">
            <h2 className="survey-questions-page-header">{steps?.[activeStep]?.label}</h2>
            <GeneralSurveyModalContent
              generalSurveyQuestions={surveyQuestion[steps?.[activeStep]?.label]}
              label={steps?.[activeStep]?.label}
              surveyQuestion={surveyQuestion}
              handleContinue={handleContinue}
              isLastStep={isLastStep}
            />
          </div>
        )}
        {!isOpen && isCurrentSurveyCompleted && (
          <div className="survey-questions-page-wrapper survey-questions-thank-you-wrapper">
            <img
              src="https://res.cloudinary.com/amunoali/image/upload/v1739819143/Survey.png"
              height="250"
              width="250"
            />
            <h2>{getSurveyQuestionsCompletedHeader(steps?.[activeStep]?.label)}</h2>
            <h4>{THANK_YOU_MESSAGE}</h4>
          </div>
        )}
      </div>
      <Footer currentPath={'/survey'} />
    </div>
  );
};
